import { skipToken } from '@reduxjs/toolkit/query';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalStorage } from 'usehooks-ts';
import { useSearchParams } from 'react-router-dom';
import { useGetApiArticlesReferencesQuery } from '../../../redux/store/api/api';
import {
  selectActiveVersion,
  selectVersionContentProcessing,
  setActiveReferenceListKey,
} from '../../../redux/store/content/slice';
import {
  articleIdUrlParam,
  EDIT_MODE_KEY,
  externalReferencesAccordionKey,
  REFERENCE_LIST_ITEM_ID_EXTENSION,
  VERSION_CONTENT_CONTAINER_ID,
} from '../../../shared/constants';
import {
  addButtonsToImportedLinks,
  removeButtonsFromImportedLinks,
} from '../functions/addButtonsToImportedLinks';
import { iconBtnIdExtension, linkButtonClass } from '../functions/makeContent';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';

/**
 * In edit mode, buttons are added to the content links.
 * When clicked (or accessed via keyboard), these buttons set focus on the corresponding `ArticleReference` object in the reference list.
 * If the containing accordion is not already open, it will also open the accordion.
 * This hook adds the necessary listeners to achieve this functionality.
 */
function useMakeAnchorButtonsClickable() {
  const { t: translation } = useTranslation();
  const dispatch = useAppDispatch();
  const version = useAppSelector(selectActiveVersion);
  const [editModeIsActive] = useLocalStorage<boolean>(EDIT_MODE_KEY, false);
  const [searchParams] = useSearchParams();
  const versionContentIsProcessing = useAppSelector(
    selectVersionContentProcessing,
  );

  const articleId: string | null = searchParams.get(articleIdUrlParam);
  const { data: referenceData } = useGetApiArticlesReferencesQuery(
    articleId && version.isRecentVersion && version.id
      ? {
          articleId,
          versionId: version.id,
        }
      : skipToken,
  );
  const articleReferencesWithCategoryId = useMemo(
    () =>
      version.isRecentVersion
        ? referenceData?.resultObject?.articleReferenceLists?.flatMap(
            (rL) =>
              rL.articleReferences?.map((r) => ({
                reference: r,
                categoryTypeId: rL.categoryType?.id || '',
              })),
          )
        : [],
    [referenceData, version.isRecentVersion],
  );

  const externalReferences = referenceData?.resultObject?.externalReferences;

  useEffect(() => {
    const openCorrespondingAccordion = async (refId: string) => {
      const articleRef = articleReferencesWithCategoryId?.find(
        (r) => r?.reference.id === refId,
      );
      if (articleRef) {
        dispatch(setActiveReferenceListKey(articleRef.categoryTypeId));
        return;
      }

      const externalRef = externalReferences?.find((r) => r?.id === refId);
      if (externalRef) {
        dispatch(setActiveReferenceListKey(externalReferencesAccordionKey));
      }
    };

    const focusRefInBox = (e: Event) => {
      const targetRefId =
        (
          (e.target as HTMLElement).id ||
          (e.target as HTMLElement).parentElement?.id ||
          ''
        ).replace(iconBtnIdExtension, '') + REFERENCE_LIST_ITEM_ID_EXTENSION;
      const refEl = document.getElementById(targetRefId);

      if (refEl) {
        openCorrespondingAccordion(
          targetRefId.replace(REFERENCE_LIST_ITEM_ID_EXTENSION, ''),
        ).then(() => {
          const linkEl = refEl.getElementsByTagName('a')[0];

          if (linkEl) {
            linkEl.focus();
          } else {
            refEl.focus();
          }
        });
      }
    };

    if (editModeIsActive) {
      addButtonsToImportedLinks({
        iconClassName: 'icon-verlinkungen',
        description: translation('showReferenceAssociatedWithLink'),
      });
    }

    if (!editModeIsActive || !version.isRecentVersion) {
      removeButtonsFromImportedLinks();
    }

    const linkIconBtns = Array.from(
      document
        .getElementById(VERSION_CONTENT_CONTAINER_ID)
        ?.querySelectorAll(`.${linkButtonClass}`) || [],
    );

    if (linkIconBtns.length > 0 && editModeIsActive) {
      linkIconBtns.forEach((btn) => {
        btn.addEventListener('click', focusRefInBox);
      });
    }

    return () => {
      if (linkIconBtns.length > 0 && editModeIsActive) {
        linkIconBtns.forEach((btn) => {
          btn.removeEventListener('click', focusRefInBox);
        });
      }
    };
  }, [referenceData, versionContentIsProcessing, editModeIsActive]);
}

export default useMakeAnchorButtonsClickable;
